/** Recipe/SKU → labor payout for the plant pay list (not CLB bay/piece-rate). */

export type LaborPayReviewSide = "ops" | "plant";

export type LaborPayStatus =
  | "open"
  | "ops_ok"
  | "plant_ok"
  | "approved"
  | "void"
  | "payment_requested"
  | "batched";

export const LABOR_PAY_OPS_ROLES = [
  "manufacturing",
  "ops_reviewer",
  "owner",
  "admin",
  "org_admin",
  "org_owner",
  "platform_owner",
  "platform_admin",
  "platform_dev",
] as const;

export const LABOR_PAY_PLANT_ROLES = [
  "manufacturing_plant_manager",
  "manufacturing_plant_supervisor",
] as const;

export function canStampLaborPay(roles: readonly string[], side: LaborPayReviewSide): boolean {
  const set = side === "ops" ? LABOR_PAY_OPS_ROLES : LABOR_PAY_PLANT_ROLES;
  return roles.some((r) => (set as readonly string[]).includes(r));
}

export function canEditCompanyLaborRates(roles: readonly string[]): boolean {
  return canStampLaborPay(roles, "ops");
}

export function laborPayoutFromRateItems(
  items: readonly {
    id: string;
    recipe_id: string | null;
    is_active: boolean;
    flat_rate_usd: number;
  }[],
  recipeId: string | null
): { payout_usd: number; rate_item_ids: string[] } {
  if (!recipeId) return { payout_usd: 0, rate_item_ids: [] };
  const matched = items.filter((i) => i.is_active && i.recipe_id === recipeId);
  const payout_usd = Math.round(matched.reduce((s, i) => s + (Number(i.flat_rate_usd) || 0), 0) * 100) / 100;
  return { payout_usd, rate_item_ids: matched.map((i) => i.id) };
}

export function nextLaborPayStatus(
  current: LaborPayStatus,
  side: LaborPayReviewSide
): LaborPayStatus {
  if (
    current === "approved" ||
    current === "void" ||
    current === "payment_requested" ||
    current === "batched"
  ) {
    return current;
  }
  if (side === "ops") return current === "plant_ok" ? "approved" : "ops_ok";
  return current === "ops_ok" ? "approved" : "plant_ok";
}

export function canRequestLaborPayment(
  roles: readonly string[],
  status: LaborPayStatus
): boolean {
  if (status !== "approved") return false;
  return canStampLaborPay(roles, "ops") || canStampLaborPay(roles, "plant");
}

export function canCloseLaborPayWeek(roles: readonly string[]): boolean {
  return canStampLaborPay(roles, "ops");
}

export function laborPayInvoiceFrozen(status: LaborPayStatus): boolean {
  return (
    status === "approved" ||
    status === "void" ||
    status === "payment_requested" ||
    status === "batched"
  );
}

export function laborPayStatusLabel(status: LaborPayStatus): string {
  if (status === "ops_ok") return "Ops OK";
  if (status === "plant_ok") return "Labor OK";
  if (status === "approved") return "Approved";
  if (status === "payment_requested") return "Payment requested";
  if (status === "batched") return "Batched";
  if (status === "void") return "Void";
  return "Open";
}
